/**
 * Gating logic for iii Agent Hub public-facing widgets (Sam support chat,
 * Ask-AI). These belong ONLY on public / unauthenticated marketing pages:
 *
 *  - The authenticated app already ships its own support bubble (Kodey), so a
 *    second chat would collide and harass signed-in customers.
 *  - The voice lead-gen gate follows the same rule (public, logged-out only).
 *
 * Kept as a pure function so the show/hide decision is unit-testable without a
 * DOM or the auth provider.
 */

/** Locale prefixes that may lead a pathname (mirrors SUPPORTED_LOCALES). */
export const LOCALE_PREFIXES = ["en", "es", "zh"] as const;

/**
 * Route prefixes that constitute the authenticated app / auth surfaces.
 * Mirrors the denylist used by the voice gate in index.html (plus /admin).
 */
export const APP_ROUTE_PREFIXES = [
  "/dashboard",
  "/advisor",
  "/admin",
  "/auth",
  "/onboarding",
  "/intake",
  "/strategy",
  "/net-worth",
  "/documents",
  "/discovery",
  "/discovery-inbox",
  "/case-intelligence",
  "/correspondence",
  "/children",
  "/goals",
  "/settings",
  "/ask-sage",
  "/support-calculator",
  "/forgot-password",
  "/reset-password",
  "/archive",
  "/opponent",
  "/businesses",
  "/tasks",
  "/my-tasks",
] as const;

/** Strip a leading locale segment (e.g. "/es/pricing" -> "/pricing"). */
export function stripLocalePrefix(pathname: string): string {
  for (const locale of LOCALE_PREFIXES) {
    if (pathname === `/${locale}`) return "/";
    if (pathname.startsWith(`/${locale}/`)) return pathname.slice(locale.length + 1);
  }
  return pathname;
}

/** True when `pathname` is part of the authenticated app / auth surfaces. */
export function isAppRoute(pathname: string): boolean {
  const path = stripLocalePrefix(pathname);
  return APP_ROUTE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * Decide whether the public hub widgets should be mounted.
 * Show only when: auth state is resolved, no user is signed in, and the route
 * is a public (non-app) route.
 */
export function shouldShowPublicWidgets(params: {
  pathname: string;
  isAuthenticated: boolean;
  loading: boolean;
}): boolean {
  const { pathname, isAuthenticated, loading } = params;
  if (loading) return false;
  if (isAuthenticated) return false;
  return !isAppRoute(pathname);
}
